export type OrderTab = "new" | "scheduled" | "current" | "completed" | "cancelled";

export interface OrderTimeline {
  time: string;
  label: string;
  status?: "done" | "current" | "pending";
}

export interface OrderOption {
  label: string;
  price: string;
}

export interface CleaningElement {
  icon: string;
  name: string;
  count: number;
  price: string;
}

export interface LaundryItem {
  icon: string;
  name: string;
  service: string;
  options?: string;
  washType: string;
  count: number;
  price: string;
}

export interface LaundryDelivery {
  type: "عادي" | "مستعجل";
  typeLabel: string;
  fee: string;
  pickupAddress: string;
  deliveryAddress: string;
  expectedDelivery: string;
}

export interface LaundryCourier {
  name: string;
  status: string;
  statusTone: "teal" | "gray" | "blue";
}

export interface Order {
  id: string;
  customer: string;
  serviceType: "غسيل سيارات" | "تنظيف منازل" | "مغاسل ملابس";
  service: string;
  details: string;
  schedule: string;
  amount: string;
  tab: OrderTab;
  countdown?: string;
  countdownPercent?: number;
  statusLabel?: string;
  assignee?: string;
  rating?: number;
  commission?: string;
  net?: string;
  cancelReason?: string;
  refund?: string;
  completedDate?: string;
  cancelDate?: string;
  scheduleCountdown?: string;
  phone: string;
  address: string;
  serviceLines: string[];
  timeline: OrderTimeline[];
  ratingComment?: string;
  date?: string;
  carType?: string;
  carPlate?: string;
  carPhoto?: string;
  locationNote?: string;
  serviceDuration?: string;
  basePrice?: string;
  options?: OrderOption[];
  settlement?: string;
  proofBefore?: string;
  proofAfter?: string;
  assignedTech?: string;
  cleaningType?: "شامل" | "بالساعة";
  cleaningElements?: CleaningElement[];
  hours?: string;
  hourlyRate?: string;
  customerNotes?: string;
  laundryItems?: LaundryItem[];
  laundrySubtotal?: string;
  delivery?: LaundryDelivery;
  pickupCourier?: LaundryCourier;
  deliveryCourier?: LaundryCourier;
  pickupPhotos?: string[];
  deliveryPhotos?: string[];
  courierBonus?: string;
}

export const orderTabs: { key: OrderTab; label: string }[] = [
  { key: "new", label: "جديد" },
  { key: "scheduled", label: "مجدول" },
  { key: "current", label: "حالي" },
  { key: "completed", label: "مكتمل" },
  { key: "cancelled", label: "ملغي" },
];

export const tabHardCounts: Record<OrderTab, number> = {
  new: 3,
  scheduled: 2,
  current: 4,
  completed: 47,
  cancelled: 3,
};

export const orders: Order[] = [
  // ═══ جديد (3) ═══
  {
    id: "GH-2853",
    customer: "أحمد محمد",
    serviceType: "غسيل سيارات",
    service: "غسيل سيارات",
    details: "غسل خارجي وداخلي — كامري 2023",
    schedule: "اليوم — في أقرب وقت",
    amount: "70",
    tab: "new",
    countdown: "4:32",
    countdownPercent: 72,
    phone: "0551234567",
    address: "حي النرجس، شارع الأمير محمد، الرياض",
    locationNote: "السيارة في الموقف الأرضي، الجهة اليمنى",
    date: "14 أغسطس 2026 — 10:30 ص",
    carType: "كامري 2023 — سيدان",
    carPlate: "أ ب د ١٢٣٤",
    carPhoto: "https://readdy.ai/api/search-image?query=A%20clean%20silver%20Toyota%20Camry%20sedan%20car%20parked%20in%20a%20bright%20parking%20garage%2C%20simple%20neutral%20background%2C%20professional%20automotive%20photography%2C%20soft%20natural%20lighting%2C%20side%20view%20angle%2C%20high%20detail%2C%20clean%20minimal%20composition&width=640&height=400&seq=car-gh2853&orientation=landscape",
    serviceDuration: "45 دقيقة",
    basePrice: "55",
    options: [{ label: "تعطير داخلي", price: "15" }],
    settlement: "ضمن تسوية 1-15 أغسطس",
    serviceLines: ["غسل خارجي وداخلي", "نوع السيارة: كامري 2023", "لوحة: أ ب د ١٢٣٤"],
    timeline: [{ time: "10:30 ص", label: "تم استلام الطلب" }],
  },
  {
    id: "GH-2854",
    customer: "سارة العتيبي",
    serviceType: "تنظيف منازل",
    service: "تنظيف منازل",
    details: "تنظيف شامل — 6 عناصر",
    schedule: "الأربعاء 14/8 — 10:00 ص",
    amount: "385",
    tab: "new",
    countdown: "12:15",
    countdownPercent: 35,
    phone: "0557654321",
    address: "حي النرجس، الرياض",
    cleaningType: "شامل",
    cleaningElements: [
      { icon: "ri-hotel-bed-line", name: "غرفة نوم", count: 2, price: "100" },
      { icon: "ri-hotel-bed-line", name: "غرفة نوم مع حمام", count: 1, price: "70" },
      { icon: "ri-sofa-line", name: "صالة", count: 1, price: "60" },
      { icon: "ri-restaurant-line", name: "مطبخ", count: 1, price: "80" },
      { icon: "ri-drop-line", name: "دورة مياه", count: 1, price: "35" },
    ],
    customerNotes: "عندي حيوان أليف، الأثاث حساس",
    serviceLines: ["غرفة نوم × 2", "صالة × 1", "مطبخ × 1", "حمام × 2"],
    timeline: [{ time: "4:35 م", label: "تم استلام الطلب" }],
  },
  {
    id: "GH-2855",
    customer: "فاطمة المالكي",
    serviceType: "مغاسل ملابس",
    service: "مغاسل ملابس",
    details: "4 قطع — غسيل وكوي + تنظيف جاف",
    schedule: "اليوم",
    amount: "83",
    tab: "new",
    countdown: "8:45",
    countdownPercent: 48,
    phone: "0559876543",
    address: "حي الياسمين، الرياض",
    laundryItems: [
      { icon: "ri-t-shirt-air-line", name: "عباءة", service: "غسيل وكوي", washType: "عادي", count: 1, price: "20" },
      { icon: "ri-t-shirt-2-line", name: "قميص", service: "غسيل وكوي", washType: "عادي", count: 2, price: "24" },
      { icon: "ri-t-shirt-2-line", name: "بنطلون", service: "تنظيف جاف", washType: "عادي", count: 1, price: "24" },
    ],
    laundrySubtotal: "68",
    delivery: { type: "عادي", typeLabel: "عادي — 48-72 ساعة", fee: "15", pickupAddress: "البيت — حي الياسمين، الرياض", deliveryAddress: "نفس العنوان", expectedDelivery: "السبت 16 أغسطس" },
    serviceLines: ["عباءة × 1 — غسيل وكوي", "قميص × 2 — غسيل وكوي", "بنطلون × 1 — تنظيف جاف"],
    timeline: [{ time: "4:50 م", label: "تم استلام الطلب", status: "done" }],
  },

  // ═══ مجدول (2) ═══
  {
    id: "GH-2848",
    customer: "لمى الشمري",
    serviceType: "غسيل سيارات",
    service: "تلميع كامل",
    details: "تلميع خارجي — لكزس 2024",
    schedule: "الخميس 15/8 — 9:00 ص",
    amount: "150",
    tab: "scheduled",
    statusLabel: "مؤكد",
    scheduleCountdown: "بعد يومين",
    phone: "0553332211",
    address: "حي حطين، الرياض",
    serviceLines: ["تلميع كامل", "نوع السيارة: SUV"],
    timeline: [
      { time: "أمس 8:00 م", label: "تم استلام الطلب" },
      { time: "أمس 8:30 م", label: "تم تأكيد الموعد" },
    ],
  },
  {
    id: "GH-2856",
    customer: "عبدالله السالم",
    serviceType: "مغاسل ملابس",
    service: "مغاسل — 10 قطع",
    details: "10 قطع — غسيل وكوي + توصيل",
    schedule: "الخميس 15/8 — 11:00 ص",
    amount: "210",
    tab: "scheduled",
    statusLabel: "بانتظار التأكيد",
    scheduleCountdown: "بعد يومين",
    phone: "0554443322",
    address: "حي العليا، الرياض",
    laundryItems: [
      { icon: "ri-t-shirt-line", name: "ثوب", service: "غسيل وكوي", washType: "عادي", count: 2, price: "40" },
      { icon: "ri-t-shirt-line", name: "شماغ", service: "غسيل وكوي", washType: "عادي", count: 2, price: "38" },
      { icon: "ri-t-shirt-2-line", name: "قميص", service: "غسيل وكوي", washType: "عادي", count: 4, price: "72" },
      { icon: "ri-t-shirt-2-line", name: "بنطلون", service: "غسيل وكوي", washType: "عادي", count: 2, price: "30" },
    ],
    laundrySubtotal: "180",
    delivery: { type: "مستعجل", typeLabel: "مستعجل — 24 ساعة", fee: "30", pickupAddress: "المكتب — حي العليا، الرياض", deliveryAddress: "نفس العنوان", expectedDelivery: "الجمعة 15 أغسطس" },
    serviceLines: ["ثوب × 2", "قميص × 4", "عباءة × 2", "بنطلون × 2"],
    timeline: [{ time: "اليوم 1:00 م", label: "تم استلام الطلب", status: "done" }],
  },

  // ═══ حالي (4) ═══
  {
    id: "GH-2846",
    customer: "نوف العنزي",
    serviceType: "تنظيف منازل",
    service: "عاملات بالساعة",
    details: "4 ساعات — تنظيف منزل",
    schedule: "اليوم 3:00 م",
    amount: "180",
    tab: "current",
    statusLabel: "الفريق في الطريق",
    assignee: "فريق سارة",
    phone: "0555556677",
    address: "حي الربوة، الرياض",
    cleaningType: "بالساعة",
    hours: "4",
    hourlyRate: "45",
    serviceLines: ["عاملات بالساعة", "المدة: 4 ساعات"],
    timeline: [
      { time: "2:00 م", label: "تم قبول الطلب" },
      { time: "2:45 م", label: "الفريق انطلق" },
    ],
  },
  {
    id: "GH-2850",
    customer: "خالد الحربي",
    serviceType: "تنظيف منازل",
    service: "تنظيف شامل",
    details: "5 غرف + مجلس + مطبخ",
    schedule: "اليوم 2:30 م",
    amount: "385",
    tab: "current",
    statusLabel: "قيد التنفيذ",
    assignee: "فريق أحمد",
    phone: "0556667788",
    address: "حي الملقا، الرياض",
    cleaningType: "شامل",
    cleaningElements: [
      { icon: "ri-hotel-bed-line", name: "غرفة نوم", count: 3, price: "150" },
      { icon: "ri-hotel-bed-line", name: "غرفة نوم مع حمام", count: 2, price: "140" },
      { icon: "ri-sofa-line", name: "مجلس", count: 1, price: "55" },
      { icon: "ri-restaurant-line", name: "مطبخ", count: 1, price: "40" },
    ],
    serviceLines: ["غرفة نوم × 3", "غرفة نوم مع حمام × 2", "مجلس × 1", "مطبخ × 1"],
    proofBefore: "https://readdy.ai/api/search-image?query=A%20slightly%20cluttered%20bedroom%20interior%20before%20professional%20home%20cleaning%20with%20an%20unmade%20bed%20and%20scattered%20items%20on%20the%20nightstand%2C%20warm%20neutral%20daylight%20from%20a%20window%2C%20realistic%20interior%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=640&height=400&seq=proof-before-gh2850&orientation=landscape",
    timeline: [
      { time: "1:00 م", label: "تم قبول الطلب" },
      { time: "1:30 م", label: "الفريق انطلق" },
      { time: "2:15 م", label: "بدأ التنفيذ" },
    ],
  },
  {
    id: "GH-2849",
    customer: "سارة العتيبي",
    serviceType: "مغاسل ملابس",
    service: "مغاسل — 4 قطع",
    details: "4 قطع — غسيل وكوي + تنظيف جاف",
    schedule: "اليوم 2:00 م",
    amount: "83",
    tab: "current",
    statusLabel: "قيد الغسيل",
    assignee: "أحمد الفيومي",
    phone: "0557778899",
    address: "حي النرجس، الرياض",
    laundryItems: [
      { icon: "ri-t-shirt-line", name: "ثوب", service: "غسيل وكوي", washType: "عادي", count: 2, price: "24" },
      { icon: "ri-t-shirt-line", name: "شماغ", service: "غسيل وكوي", options: "نشا ثقيل + مرزام", washType: "عادي", count: 1, price: "19" },
      { icon: "ri-t-shirt-air-line", name: "جاكيت", service: "تنظيف جاف", washType: "عادي", count: 1, price: "25" },
    ],
    laundrySubtotal: "68",
    delivery: { type: "عادي", typeLabel: "عادي — 48-72 ساعة", fee: "15", pickupAddress: "البيت — حي النرجس، الرياض", deliveryAddress: "نفس العنوان", expectedDelivery: "الأربعاء 14 أغسطس" },
    pickupCourier: { name: "أحمد الفيومي", status: "تم الاستلام من العميل", statusTone: "teal" },
    deliveryCourier: { name: "محمد السبيعي", status: "بانتظار جهوزية القطع", statusTone: "gray" },
    pickupPhotos: [
      "https://readdy.ai/api/search-image?query=A%20neatly%20folded%20crisp%20white%20traditional%20thobe%20garment%20placed%20on%20a%20clean%20wooden%20surface%20during%20laundry%20pickup%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-pickup-thobe&orientation=squarish",
      "https://readdy.ai/api/search-image?query=A%20neatly%20folded%20red%20and%20white%20checkered%20shmagh%20headscarf%20placed%20on%20a%20clean%20wooden%20surface%20during%20laundry%20pickup%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-pickup-shmagh&orientation=squarish",
      "https://readdy.ai/api/search-image?query=A%20dark%20navy%20suit%20jacket%20hanging%20on%20a%20wooden%20hanger%20during%20laundry%20pickup%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-pickup-jacket&orientation=squarish",
    ],
    serviceLines: ["ثوب × 2", "شماغ × 1", "جاكيت × 1"],
    timeline: [
      { time: "10:30 ص", label: "تم استلام الطلب", status: "done" },
      { time: "10:35 ص", label: "تم قبول الطلب", status: "done" },
      { time: "10:40 ص", label: "مندوب الاستلام في الطريق — أحمد الفيومي", status: "done" },
      { time: "10:52 ص", label: "المندوب وصل لعنوان العميل", status: "done" },
      { time: "10:55 ص", label: "تم استلام 4 قطع + تصوير", status: "done" },
      { time: "11:20 ص", label: "القطع وصلت المغسلة", status: "done" },
      { time: "الآن", label: "قيد الغسيل", status: "current" },
      { time: "", label: "جاهزة للتسليم", status: "pending" },
      { time: "", label: "مندوب التسليم في الطريق", status: "pending" },
      { time: "", label: "تم التسليم", status: "pending" },
    ],
  },
  {
    id: "GH-2857",
    customer: "محمد السهلي",
    serviceType: "مغاسل ملابس",
    service: "مغاسل — 3 قطع",
    details: "3 قطع — غسيل وكوي",
    schedule: "اليوم 3:30 م",
    amount: "65",
    tab: "current",
    statusLabel: "مندوب الاستلام في الطريق",
    assignee: "خالد الفيومي",
    phone: "0558889900",
    address: "حي الورود، الرياض",
    laundryItems: [
      { icon: "ri-t-shirt-line", name: "ثوب", service: "غسيل وكوي", washType: "عادي", count: 1, price: "12" },
      { icon: "ri-t-shirt-2-line", name: "قميص", service: "غسيل وكوي", washType: "عادي", count: 2, price: "38" },
    ],
    laundrySubtotal: "50",
    delivery: { type: "عادي", typeLabel: "عادي — 48-72 ساعة", fee: "15", pickupAddress: "البيت — حي الورود، الرياض", deliveryAddress: "نفس العنوان", expectedDelivery: "الخميس 15 أغسطس" },
    pickupCourier: { name: "خالد الفيومي", status: "في الطريق للعميل", statusTone: "teal" },
    serviceLines: ["ثوب × 1", "قميص × 2"],
    timeline: [
      { time: "2:30 م", label: "تم قبول الطلب", status: "done" },
      { time: "3:00 م", label: "مندوب الاستلام في الطريق", status: "current" },
    ],
  },

  // ═══ مكتمل (47) — 12 mock items ═══
  {
    id: "GH-2845",
    customer: "أحمد محمد",
    serviceType: "غسيل سيارات",
    service: "غسل خارجي وداخلي",
    details: "غسل كامل — النترا 2022",
    schedule: "اليوم 1:00 م",
    amount: "70",
    tab: "completed",
    rating: 5,
    commission: "10.50",
    net: "59.50",
    completedDate: "2 سبتمبر 2026",
    phone: "0558889900",
    address: "حي النخيل، الرياض",
    locationNote: "لا يوجد",
    date: "2 سبتمبر 2026 — 10:30 ص",
    carType: "النترا 2022 — سيدان",
    carPlate: "أ هـ س ٤٥٦٧",
    carPhoto: "https://readdy.ai/api/search-image?query=A%20clean%20white%20Hyundai%20Elantra%20sedan%20car%20parked%20in%20a%20bright%20car%20wash%20bay%2C%20simple%20neutral%20background%2C%20professional%20automotive%20photography%2C%20soft%20natural%20lighting%2C%20side%20view%20angle%2C%20high%20detail%2C%20clean%20minimal%20composition&width=640&height=400&seq=car-gh2845&orientation=landscape",
    serviceDuration: "45 دقيقة",
    basePrice: "55",
    options: [{ label: "تعطير داخلي", price: "15" }],
    settlement: "ضمن تسوية 1-15 أغسطس",
    assignedTech: "أحمد محمد — فريق أ",
    proofBefore: "https://readdy.ai/api/search-image?query=A%20sedan%20car%20with%20dust%20and%20dirt%20on%20the%20body%20surface%20before%20washing%2C%20parked%20in%20a%20car%20wash%20bay%2C%20simple%20neutral%20background%2C%20realistic%20photo%2C%20soft%20lighting%2C%20side%20view%2C%20high%20detail&width=640&height=400&seq=proof-before-gh2845&orientation=landscape",
    proofAfter: "https://readdy.ai/api/search-image?query=A%20shiny%20clean%20sedan%20car%20with%20wet%20glossy%20surface%20after%20professional%20car%20wash%2C%20water%20droplets%2C%20parked%20in%20a%20car%20wash%20bay%2C%20simple%20neutral%20background%2C%20realistic%20photo%2C%20soft%20lighting%2C%20side%20view%2C%20high%20detail&width=640&height=400&seq=proof-after-gh2845&orientation=landscape",
    serviceLines: ["غسل خارجي وداخلي", "نوع السيارة: سيدان"],
    timeline: [
      { time: "10:30 ص", label: "تم استلام الطلب" },
      { time: "10:32 ص", label: "تم قبول الطلب" },
      { time: "10:33 ص", label: "تم الإسناد لـ أحمد" },
      { time: "10:45 ص", label: "المنفذ في الطريق" },
      { time: "11:00 ص", label: "وصل + صورة قبل" },
      { time: "11:42 ص", label: "تم الانتهاء + صورة بعد" },
    ],
    ratingComment: "شغل ممتاز — السيارة طلعت تلمع 👍",
  },
  {
    id: "GH-2844",
    customer: "هند المطيري",
    serviceType: "مغاسل ملابس",
    service: "مغاسل — 8 قطع",
    details: "8 قطع — غسيل وكوي + توصيل",
    schedule: "أمس 5:00 م",
    amount: "160",
    tab: "completed",
    rating: 4,
    commission: "24",
    net: "136",
    completedDate: "1 سبتمبر 2026",
    phone: "0559990011",
    address: "حي الورود، الرياض",
    laundryItems: [
      { icon: "ri-t-shirt-line", name: "ثوب", service: "غسيل وكوي", washType: "عادي", count: 3, price: "60" },
      { icon: "ri-t-shirt-2-line", name: "قميص", service: "غسيل وكوي", washType: "عادي", count: 3, price: "60" },
      { icon: "ri-t-shirt-2-line", name: "بنطلون", service: "غسيل وكوي", washType: "عادي", count: 2, price: "25" },
    ],
    laundrySubtotal: "145",
    delivery: { type: "عادي", typeLabel: "عادي — 48-72 ساعة", fee: "15", pickupAddress: "البيت — حي الورود، الرياض", deliveryAddress: "نفس العنوان", expectedDelivery: "أمس 6:00 م" },
    pickupCourier: { name: "أحمد الفيومي", status: "تم الاستلام", statusTone: "teal" },
    deliveryCourier: { name: "محمد السبيعي", status: "تم التسليم", statusTone: "teal" },
    pickupPhotos: [
      "https://readdy.ai/api/search-image?query=A%20neatly%20folded%20crisp%20white%20traditional%20thobe%20garment%20placed%20on%20a%20clean%20wooden%20surface%20during%20laundry%20pickup%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-pickup-thobe&orientation=squarish",
      "https://readdy.ai/api/search-image?query=A%20neatly%20folded%20red%20and%20white%20checkered%20shmagh%20headscarf%20placed%20on%20a%20clean%20wooden%20surface%20during%20laundry%20pickup%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-pickup-shmagh&orientation=squarish",
      "https://readdy.ai/api/search-image?query=A%20dark%20navy%20suit%20jacket%20hanging%20on%20a%20wooden%20hanger%20during%20laundry%20pickup%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-pickup-jacket&orientation=squarish",
    ],
    deliveryPhotos: [
      "https://readdy.ai/api/search-image?query=A%20freshly%20pressed%20white%20thobe%20garment%20wrapped%20in%20clear%20plastic%20delivery%20packaging%20hanging%20on%20a%20hanger%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-delivery-thobe&orientation=squarish",
      "https://readdy.ai/api/search-image?query=A%20neat%20stack%20of%20freshly%20pressed%20folded%20shirts%20wrapped%20in%20clear%20plastic%20laundry%20packaging%20on%20a%20clean%20surface%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-delivery-shirts&orientation=squarish",
      "https://readdy.ai/api/search-image?query=Several%20freshly%20cleaned%20garments%20hanging%20neatly%20on%20hangers%20in%20clear%20plastic%20covers%20ready%20for%20delivery%2C%20soft%20warm%20natural%20lighting%2C%20simple%20neutral%20beige%20background%2C%20realistic%20product%20photography%2C%20high%20detail%2C%20clean%20minimal%20composition&width=400&height=400&seq=laundry-delivery-hanging&orientation=squarish",
    ],
    courierBonus: "4",
    serviceLines: ["ثوب × 3", "قميص × 3", "بنطلون × 2"],
    timeline: [
      { time: "أمس 10:00 ص", label: "تم استلام الطلب", status: "done" },
      { time: "أمس 10:10 ص", label: "تم قبول الطلب", status: "done" },
      { time: "أمس 11:00 ص", label: "تم استلام القطع + تصوير", status: "done" },
      { time: "أمس 3:00 م", label: "تم التسليم", status: "done" },
    ],
    ratingComment: "ممتاز بس التوصيل تأخر شوي",
  },
  {
    id: "GH-2843",
    customer: "تركي الشهري",
    serviceType: "غسيل سيارات",
    service: "غسل خارجي",
    details: "غسل خارجي — بيك أب",
    schedule: "أمس 2:00 م",
    amount: "35",
    tab: "completed",
    rating: 5,
    commission: "5.25",
    net: "29.75",
    completedDate: "1 سبتمبر 2026",
    phone: "0551122334",
    address: "حي الصحافة، الرياض",
    serviceLines: ["غسل خارجي", "نوع السيارة: بيك أب"],
    timeline: [
      { time: "أمس 1:30 م", label: "تم قبول الطلب" },
      { time: "أمس 2:10 م", label: "اكتمل الطلب" },
    ],
  },
  {
    id: "GH-2841",
    customer: "جواهر العجمي",
    serviceType: "تنظيف منازل",
    service: "تنظيف شامل",
    details: "4 غرف + صالتين + مطبخ",
    schedule: "أمس 10:00 ص",
    amount: "340",
    tab: "completed",
    rating: 5,
    commission: "51",
    net: "289",
    completedDate: "1 سبتمبر 2026",
    phone: "0552233445",
    address: "حي المروج، الرياض",
    cleaningType: "شامل",
    cleaningElements: [
      { icon: "ri-hotel-bed-line", name: "غرفة نوم", count: 3, price: "150" },
      { icon: "ri-hotel-bed-line", name: "غرفة نوم مع حمام", count: 1, price: "70" },
      { icon: "ri-sofa-line", name: "صالة", count: 2, price: "80" },
      { icon: "ri-restaurant-line", name: "مطبخ", count: 1, price: "40" },
    ],
    customerNotes: "عندي حيوان أليف، الأثاث حساس",
    assignedTech: "فريق أ — 3 أشخاص",
    proofBefore: "https://readdy.ai/api/search-image?query=A%20cluttered%20living%20room%20interior%20before%20professional%20home%20cleaning%2C%20sofa%20with%20scattered%20cushions%2C%20items%20on%20coffee%20table%2C%20visible%20dust%2C%20warm%20neutral%20lighting%2C%20realistic%20interior%20photo%2C%20high%20detail&width=640&height=400&seq=proof-before-gh2841&orientation=landscape",
    proofAfter: "https://readdy.ai/api/search-image?query=A%20spotless%20clean%20modern%20living%20room%20interior%20after%20professional%20home%20cleaning%2C%20neatly%20arranged%20sofa%20with%20plump%20cushions%2C%20sparkling%20clean%20surfaces%2C%20bright%20natural%20light%2C%20realistic%20interior%20photo%2C%20high%20detail&width=640&height=400&seq=proof-after-gh2841&orientation=landscape",
    serviceLines: ["غرفة نوم × 3", "غرفة نوم مع حمام × 1", "صالة × 2", "مطبخ × 1"],
    timeline: [
      { time: "10:00 ص", label: "تم استلام الطلب" },
      { time: "10:05 ص", label: "تم قبول الطلب" },
      { time: "10:06 ص", label: "تم الإسناد لـ فريق أ (3 أشخاص)" },
      { time: "10:30 ص", label: "الفريق في الطريق" },
      { time: "10:55 ص", label: "وصل + صورة قبل" },
      { time: "10:57 ص", label: "بدأ التنفيذ" },
      { time: "1:15 م", label: "تم الانتهاء + صورة بعد" },
    ],
    ratingComment: "العاملات محترمات وشغل نظيف جداً",
  },
  {
    id: "GH-2840",
    customer: "نورة السبيعي",
    serviceType: "غسيل سيارات",
    service: "تلميع كامل",
    details: "تلميع كامل — مرسيدس 2023",
    schedule: "أمس 9:00 ص",
    amount: "120",
    tab: "completed",
    rating: 4,
    commission: "18",
    net: "102",
    completedDate: "1 سبتمبر 2026",
    phone: "0553344556",
    address: "حي العزيزية، الرياض",
    serviceLines: ["تلميع كامل", "نوع السيارة: سيدان"],
    timeline: [
      { time: "أمس 8:00 ص", label: "تم قبول الطلب" },
      { time: "أمس 10:30 ص", label: "اكتمل الطلب" },
    ],
    ratingComment: "شغل نظيف بس السعر شوي غالي",
  },
  {
    id: "GH-2838",
    customer: "فهد العنزي",
    serviceType: "تنظيف منازل",
    service: "تنظيف شامل",
    details: "3 غرف + صالة + مجلس",
    schedule: "31 أغسطس 3:00 م",
    amount: "250",
    tab: "completed",
    rating: 3,
    commission: "37.50",
    net: "212.50",
    completedDate: "31 أغسطس 2026",
    phone: "0554455667",
    address: "حي الربوة، الرياض",
    cleaningType: "شامل",
    cleaningElements: [
      { icon: "ri-hotel-bed-line", name: "غرفة نوم", count: 2, price: "100" },
      { icon: "ri-sofa-line", name: "صالة", count: 1, price: "60" },
      { icon: "ri-sofa-line", name: "مجلس", count: 1, price: "50" },
      { icon: "ri-restaurant-line", name: "مطبخ", count: 1, price: "40" },
    ],
    serviceLines: ["غرفة نوم × 2", "صالة × 1", "مجلس × 1"],
    timeline: [
      { time: "31 أغسطس 2:00 م", label: "تم قبول الطلب" },
      { time: "31 أغسطس 5:30 م", label: "اكتمل الطلب" },
    ],
    ratingComment: "الخدمة مقبولة لكن التأخير مزعج",
  },
  {
    id: "GH-2836",
    customer: "ليلى القحطاني",
    serviceType: "مغاسل ملابس",
    service: "مغاسل — 6 قطع",
    details: "6 قطع — غسيل وكوي",
    schedule: "31 أغسطس 11:00 ص",
    amount: "95",
    tab: "completed",
    rating: 5,
    commission: "14.25",
    net: "80.75",
    completedDate: "31 أغسطس 2026",
    phone: "0555566778",
    address: "حي المروج، الرياض",
    laundryItems: [
      { icon: "ri-t-shirt-line", name: "ثوب", service: "غسيل وكوي", washType: "عادي", count: 2, price: "40" },
      { icon: "ri-t-shirt-2-line", name: "قميص", service: "غسيل وكوي", washType: "عادي", count: 3, price: "30" },
      { icon: "ri-t-shirt-2-line", name: "بنطلون", service: "غسيل وكوي", washType: "عادي", count: 1, price: "10" },
    ],
    laundrySubtotal: "80",
    delivery: { type: "عادي", typeLabel: "عادي — 48-72 ساعة", fee: "15", pickupAddress: "البيت — حي المروج، الرياض", deliveryAddress: "نفس العنوان", expectedDelivery: "31 أغسطس 2:00 م" },
    pickupCourier: { name: "أحمد الفيومي", status: "تم الاستلام", statusTone: "teal" },
    deliveryCourier: { name: "محمد السبيعي", status: "تم التسليم", statusTone: "teal" },
    courierBonus: "3",
    serviceLines: ["ثوب × 2", "قميص × 3", "بنطلون × 1"],
    timeline: [
      { time: "31 أغسطس 9:00 ص", label: "تم قبول الطلب", status: "done" },
      { time: "31 أغسطس 2:00 م", label: "تم التسليم", status: "done" },
    ],
  },
  {
    id: "GH-2835",
    customer: "سعد الدوسري",
    serviceType: "غسيل سيارات",
    service: "غسل خارجي وداخلي",
    details: "غسل كامل — فورد F-150",
    schedule: "30 أغسطس 6:00 م",
    amount: "85",
    tab: "completed",
    rating: 4,
    commission: "12.75",
    net: "72.25",
    completedDate: "30 أغسطس 2026",
    phone: "0556677889",
    address: "حي الملقا، الرياض",
    serviceLines: ["غسل خارجي وداخلي", "نوع السيارة: بيك أب"],
    timeline: [
      { time: "30 أغسطس 5:00 م", label: "تم قبول الطلب" },
      { time: "30 أغسطس 6:45 م", label: "اكتمل الطلب" },
    ],
  },
  {
    id: "GH-2834",
    customer: "منى الحربي",
    serviceType: "تنظيف منازل",
    service: "تنظيف شامل",
    details: "5 غرف + صالتين + مطبخ + حديقة",
    schedule: "30 أغسطس 10:00 ص",
    amount: "420",
    tab: "completed",
    rating: 5,
    commission: "63",
    net: "357",
    completedDate: "30 أغسطس 2026",
    phone: "0557788990",
    address: "حي النخيل، الرياض",
    cleaningType: "شامل",
    cleaningElements: [
      { icon: "ri-hotel-bed-line", name: "غرفة نوم", count: 3, price: "150" },
      { icon: "ri-sofa-line", name: "صالة", count: 2, price: "120" },
      { icon: "ri-restaurant-line", name: "مطبخ", count: 1, price: "80" },
      { icon: "ri-plant-line", name: "حديقة", count: 1, price: "70" },
    ],
    serviceLines: ["غرفة نوم × 3", "صالة × 2", "مطبخ × 1", "حديقة × 1"],
    timeline: [
      { time: "30 أغسطس 8:00 ص", label: "تم قبول الطلب" },
      { time: "30 أغسطس 2:00 م", label: "اكتمل الطلب" },
    ],
    ratingComment: "أفضل تجربة تنظيف جربتها في حياتي",
  },
  {
    id: "GH-2833",
    customer: "عبدالله المالكي",
    serviceType: "مغاسل ملابس",
    service: "مغاسل — 4 قطع",
    details: "4 قطع — غسيل وكوي",
    schedule: "29 أغسطس 8:00 م",
    amount: "55",
    tab: "completed",
    rating: 5,
    commission: "8.25",
    net: "46.75",
    completedDate: "29 أغسطس 2026",
    phone: "0558899001",
    address: "حي الورود، الرياض",
    laundryItems: [
      { icon: "ri-t-shirt-line", name: "ثوب", service: "غسيل وكوي", washType: "عادي", count: 1, price: "15" },
      { icon: "ri-t-shirt-2-line", name: "قميص", service: "غسيل وكوي", washType: "عادي", count: 2, price: "20" },
      { icon: "ri-t-shirt-2-line", name: "بنطلون", service: "غسيل وكوي", washType: "عادي", count: 1, price: "5" },
    ],
    laundrySubtotal: "40",
    delivery: { type: "عادي", typeLabel: "عادي — 48-72 ساعة", fee: "15", pickupAddress: "البيت — حي الورود، الرياض", deliveryAddress: "نفس العنوان", expectedDelivery: "29 أغسطس 10:00 م" },
    pickupCourier: { name: "أحمد الفيومي", status: "تم الاستلام", statusTone: "teal" },
    deliveryCourier: { name: "محمد السبيعي", status: "تم التسليم", statusTone: "teal" },
    courierBonus: "2",
    serviceLines: ["ثوب × 1", "قميص × 2", "بنطلون × 1"],
    timeline: [
      { time: "29 أغسطس 6:00 م", label: "تم قبول الطلب", status: "done" },
      { time: "29 أغسطس 10:00 م", label: "تم التسليم", status: "done" },
    ],
  },
  {
    id: "GH-2832",
    customer: "ريم الشمري",
    serviceType: "غسيل سيارات",
    service: "تلميع كامل",
    details: "تلميع كامل — رنج روفر 2024",
    schedule: "29 أغسطس 2:00 م",
    amount: "200",
    tab: "completed",
    rating: 4,
    commission: "30",
    net: "170",
    completedDate: "29 أغسطس 2026",
    phone: "0559900112",
    address: "حي العليا، الرياض",
    serviceLines: ["تلميع كامل", "نوع السيارة: SUV"],
    timeline: [
      { time: "29 أغسطس 12:00 م", label: "تم قبول الطلب" },
      { time: "29 أغسطس 4:00 م", label: "اكتمل الطلب" },
    ],
    ratingComment: "شغل ممتاز لكن التلميع استغرق وقت أطول من المتوقع",
  },
  {
    id: "GH-2831",
    customer: "ياسر العتيبي",
    serviceType: "تنظيف منازل",
    service: "تنظيف شامل",
    details: "3 غرف + صالة + مطبخ",
    schedule: "28 أغسطس 5:00 م",
    amount: "180",
    tab: "completed",
    rating: 3,
    commission: "27",
    net: "153",
    completedDate: "28 أغسطس 2026",
    phone: "0550011223",
    address: "حي الياسمين، الرياض",
    cleaningType: "شامل",
    cleaningElements: [
      { icon: "ri-hotel-bed-line", name: "غرفة نوم", count: 2, price: "100" },
      { icon: "ri-sofa-line", name: "صالة", count: 1, price: "40" },
      { icon: "ri-restaurant-line", name: "مطبخ", count: 1, price: "40" },
    ],
    serviceLines: ["غرفة نوم × 2", "صالة × 1", "مطبخ × 1"],
    timeline: [
      { time: "28 أغسطس 3:00 م", label: "تم قبول الطلب" },
      { time: "28 أغسطس 7:00 م", label: "اكتمل الطلب" },
    ],
    ratingComment: "ممتاز بس المواد المستخدمة ريحتها قوية",
  },

  // ═══ ملغي (3) ═══
  {
    id: "GH-2852",
    customer: "عمر القحطاني",
    serviceType: "غسيل سيارات",
    service: "تلميع كامل",
    details: "تلميع كامل — جي إم سي",
    schedule: "أمس 7:00 م",
    amount: "120",
    tab: "cancelled",
    cancelReason: "العميل ألغى — تغيّر الموعد",
    refund: "تم",
    cancelDate: "1 سبتمبر 2026",
    phone: "0553344556",
    address: "حي قرطبة، الرياض",
    serviceLines: ["تلميع كامل", "نوع السيارة: فان"],
    timeline: [
      { time: "أمس 5:00 م", label: "تم استلام الطلب" },
      { time: "أمس 5:40 م", label: "ألغى العميل الطلب" },
    ],
  },
  {
    id: "GH-2839",
    customer: "بدر الرشيد",
    serviceType: "مغاسل ملابس",
    service: "مغاسل — 3 قطع",
    details: "3 قطع — غسيل وكوي",
    schedule: "أول أمس 6:00 م",
    amount: "60",
    tab: "cancelled",
    cancelReason: "المندوب غير متاح — رفض من المزود",
    refund: "جاري",
    cancelDate: "31 أغسطس 2026",
    phone: "0554455667",
    address: "حي العزيزية، الرياض",
    laundryItems: [
      { icon: "ri-t-shirt-2-line", name: "قميص", service: "غسيل وكوي", washType: "عادي", count: 2, price: "30" },
      { icon: "ri-t-shirt-2-line", name: "بنطلون", service: "غسيل وكوي", washType: "عادي", count: 1, price: "15" },
    ],
    laundrySubtotal: "45",
    delivery: { type: "عادي", typeLabel: "عادي — 48-72 ساعة", fee: "15", pickupAddress: "البيت — حي العزيزية، الرياض", deliveryAddress: "نفس العنوان", expectedDelivery: "—" },
    serviceLines: ["قميص × 2", "بنطلون × 1"],
    timeline: [
      { time: "أول أمس 4:00 م", label: "تم استلام الطلب", status: "done" },
      { time: "أول أمس 4:20 م", label: "تم رفض الطلب", status: "done" },
    ],
  },
  {
    id: "GH-2837",
    customer: "مشعل الدوسري",
    serviceType: "تنظيف منازل",
    service: "عاملات بالساعة",
    details: "3 ساعات",
    schedule: "أول أمس 3:00 م",
    amount: "135",
    tab: "cancelled",
    cancelReason: "خارج نطاق التغطية",
    refund: "تم",
    cancelDate: "31 أغسطس 2026",
    phone: "0555566778",
    address: "حي الخليج، الرياض",
    cleaningType: "بالساعة",
    hours: "3",
    hourlyRate: "45",
    serviceLines: ["عاملات بالساعة", "المدة: 3 ساعات"],
    timeline: [
      { time: "أول أمس 2:00 م", label: "تم استلام الطلب" },
      { time: "أول أمس 2:10 م", label: "تم رفض الطلب" },
    ],
  },
];